/**
 * OnboardingFlow — Multi-step onboarding guide shown to new users after signup.
 * Persists completion status to Firestore so it only shows once.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../utils/firebase-config';
import { doc, updateDoc } from 'firebase/firestore';
import './OnboardingFlow.css';

const STEPS = [
  {
    icon: '🧪',
    title: 'Welcome to SoapLab!',
    description:
      'Your all-in-one soap making companion. Let us give you a quick tour of the key features so you can hit the ground running.',
    image: null,
  },
  {
    icon: '🧮',
    title: 'Lye Calculator',
    description:
      'The heart of SoapLab. Enter your oils, choose NaOH or KOH, set your superfat %, and get precise lye and water amounts instantly. It\'s free and works for cold process, hot process, and liquid soap.',
    cta: { label: 'Open Calculator', path: '/calculator' },
  },
  {
    icon: '📦',
    title: 'Inventory Management',
    description:
      'Keep track of all your oils, butters, fragrances, and additives. Set low-stock alerts so you never run out mid-batch. Add suppliers and costs to calculate accurate recipe pricing.',
    cta: { label: 'Go to Inventory', path: '/inventory' },
  },
  {
    icon: '📖',
    title: 'Recipes & Batches',
    description:
      'Save your favourite formulas as recipes, then log each time you make them as a batch. Track curing dates, add notes, and know exactly when your soap is ready to sell or gift.',
    cta: { label: 'View Recipes', path: '/recipes' },
  },
  {
    icon: '📅',
    title: 'Calendar & Notifications',
    description:
      'See your curing timeline at a glance on the calendar. Get alerts when batches are ready, stock is low, or subscriptions are expiring — so nothing slips through the cracks.',
    cta: { label: 'Open Calendar', path: '/calendar' },
  },
  {
    icon: '⭐',
    title: 'Pro Features',
    description:
      'Unlock the Recipe Generator, Print Center, Sales Dashboard, Label Generator, Lab Notes, and more with a Pro subscription. Start with the free plan and upgrade anytime!',
    cta: { label: 'See Pricing', path: '/pricing' },
  },
  {
    icon: '🚀',
    title: 'You\'re All Set!',
    description:
      'Head to your dashboard to get started. You can always explore features from the navigation bar. Happy soap making!',
    cta: { label: 'Go to Dashboard', path: '/' },
    isFinal: true,
  },
];

export default function OnboardingFlow() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const markCompleted = useCallback(async () => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingCompleted: true,
      });
    } catch (err) {
      console.error('Error marking onboarding complete:', err);
    }
  }, [user]);

  // Check if user needs onboarding
  useEffect(() => {
    if (!user || !userProfile) return;
    // Show only if onboarding not completed
    if (userProfile.onboardingCompleted) return;

    // Only trigger for accounts created in the last 24 hours
    const createdAt = userProfile.createdAt?.toDate?.();
    if (createdAt) {
      const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceCreation > 24) {
        // Old account — mark completed silently
        markCompleted();
        return;
      }
    }

    setVisible(true);
  }, [user, userProfile, markCompleted]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSkip = async () => {
    setExiting(true);
    await markCompleted();
    setTimeout(() => setVisible(false), 300);
  };

  const handleFinish = async () => {
    setExiting(true);
    await markCompleted();
    const step = STEPS[currentStep];
    setTimeout(() => {
      setVisible(false);
      if (step.cta?.path) {
        navigate(step.cta.path);
      }
    }, 300);
  };

  const handleCtaClick = () => {
    const step = STEPS[currentStep];
    if (step.isFinal) {
      handleFinish();
    } else if (step.cta?.path) {
      // Navigate but keep onboarding open — user can come back
      handleFinish();
    }
  };

  if (!visible) return null;

  const step = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className={`onboarding-overlay ${exiting ? 'onboarding-exit' : ''}`}>
      <div className="onboarding-modal">
        {/* Skip button */}
        <button className="onboarding-skip" onClick={handleSkip}>
          Skip Tour ✕
        </button>

        {/* Progress bar */}
        <div className="onboarding-progress">
          <div className="onboarding-progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Step content */}
        <div className="onboarding-step" key={currentStep}>
          <h2 className="onboarding-title">{step.title}</h2>
          <p className="onboarding-desc">{step.description}</p>

          {step.cta && (
            <button className="onboarding-cta" onClick={handleCtaClick}>
              {step.cta.label} →
            </button>
          )}
        </div>

        {/* Step dots */}
        <div className="onboarding-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}
              onClick={() => setCurrentStep(i)}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="onboarding-nav">
          <button
            className="onboarding-nav-btn"
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            Back
          </button>

          <span className="onboarding-step-count">
            {currentStep + 1} / {STEPS.length}
          </span>

          {step.isFinal ? (
            <button className="onboarding-nav-btn onboarding-nav-finish" onClick={handleFinish}>
              Get Started! 🚀
            </button>
          ) : (
            <button className="onboarding-nav-btn onboarding-nav-next" onClick={handleNext}>
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
